// Telefonnummern prüfen, aufräumen und wählbar machen.
//
// Anlass (Callcenter, 28.08.2026): „Kunden geben im Formular die Vorwahl
// doppelt ein, dadurch kann man die nicht anrufen, außer man bemerkt das und
// justiert die Nummer."
//
// Im Bestand nachgezählt (28.08.2026): 303 Nummern stehen als „+49 49 176…"
// da — die Landesvorwahl zweimal, weil das Web-Formular „+49" vorgibt und die
// Person sie noch einmal eintippt. Bei den Formular-Anfragen ist das jede
// zehnte Nummer (125 von 1.229). Vier stehen sogar mit zweitem Plus da
// („+49 +49 176…").
//
// Warum das vorher niemandem auffiel: „+49491761234567" ist technisch eine
// gültige E.164-Nummer, und selbst die Rufnummern-Bibliothek hält sie für
// gültig — sie liest sie als Festnetznummer aus Leer in Ostfriesland (0491).
// Die Prüfung ließ sie also durch, die Anlage wählte 49 49 … und der
// Anrufversuch war verbrannt, ohne dass jemand sah, warum.
//
// Aufteilung hier:
//  • Die Rufnummern-Bibliothek (libphonenumber, dieselbe Datenbasis wie bei
//    Google und WhatsApp) entscheidet, was eine gültige Nummer ist, aus
//    welchem Land sie kommt, ob Mobil oder Festnetz, und wie man sie schreibt.
//    Sie nimmt auch alle Schreibweisen an — mit 0, ohne 0, mit +49, mit 0049.
//  • Die Reparaturen hier obendrauf fangen die Eingabefehler, die die
//    Bibliothek nicht als Fehler erkennt: die doppelte Vorwahl.

import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
  type PhoneNumber,
} from 'libphonenumber-js/max';

export const STANDARD_LAND: CountryCode = 'DE';

export type NummerStatus =
  /** Kein Wert eingetragen. */
  | 'leer'
  /** Nummer ist in Ordnung. */
  | 'ok'
  /** Nummer war fehlerhaft und wurde automatisch berichtigt. */
  | 'korrigiert'
  /** Mit der Nummer stimmt etwas nicht — das muss ein Mensch ansehen. */
  | 'pruefen';

export type NummerArt = 'mobil' | 'festnetz' | 'unbekannt';

export type NummerBefund = {
  status: NummerStatus;
  /** Die Nummer, mit der weitergearbeitet werden soll — international
   *  (+49…), wenn sie sich deuten ließ. Nie leer, solange die Eingabe nicht
   *  leer war: eine unverständliche Eingabe wird gemeldet, nicht weggeworfen. */
  nummer: string;
  /** Ob die Telefonanlage damit wählen kann. */
  waehlbar: boolean;
  /** Lesbare Schreibweise für die Anzeige („0171 8179758"). */
  anzeige: string;
  /** Erkanntes Land, falls ermittelbar. */
  land: CountryCode | null;
  art: NummerArt;
  /** Klartext für die Oberfläche — null, wenn nichts zu sagen ist. */
  hinweis: string | null;
};

function istMobil(p: PhoneNumber): boolean {
  const art = p.getType();
  return art === 'MOBILE' || art === 'FIXED_LINE_OR_MOBILE';
}

function artVon(p: PhoneNumber | null): NummerArt {
  if (!p) return 'unbekannt';
  const art = p.getType();
  if (art === 'MOBILE') return 'mobil';
  if (art === 'FIXED_LINE') return 'festnetz';
  return 'unbekannt';
}

function deuten(text: string, land: CountryCode): PhoneNumber | null {
  try {
    return parsePhoneNumberFromString(text, land) ?? null;
  } catch {
    return null;
  }
}

/**
 * Prüft eine eingegebene oder gespeicherte Nummer und sagt, was mit ihr ist.
 *
 * `land` ist die Vorauswahl für Nummern ohne eigene Landesvorwahl. Steht im
 * Text selbst eine Landesvorwahl („+41 …"), gewinnt die — sonst würde aus der
 * Auswahl genau der Fehler, um den es hier geht.
 */
export function pruefeNummer(input: string, land: CountryCode = STANDARD_LAND): NummerBefund {
  const leer: NummerBefund = {
    status: 'leer', nummer: '', waehlbar: false, anzeige: '',
    land: null, art: 'unbekannt', hinweis: null,
  };

  // Trennzeichen weg. Der Schrägstrich gehört dazu („0176/1234567" steht
  // 23-mal im Bestand) — sonst gilt die Nummer als unvollständig.
  const roh = (input ?? '').replace(/[\s.\-()/]/g, '');
  if (!roh) return leer;

  // Buchstaben oder Sonderzeichen: hier ist nichts zu deuten. Der Wert bleibt
  // trotzdem stehen, damit niemandem seine Eingabe unter den Händen
  // verschwindet.
  if (/[^+\d]/.test(roh)) {
    return {
      status: 'pruefen', nummer: roh, waehlbar: false, anzeige: roh,
      land: null, art: 'unbekannt',
      hinweis: 'Das sieht nicht wie eine Telefonnummer aus — bitte prüfen.',
    };
  }

  // Nur die *Fehler* werden gemeldet. Dass „0171…" als „+49171…" gespeichert
  // wird, ist Alltag und kein Befund — sonst stünde bei 29.000 Nummern ein
  // Warnhinweis, den niemand mehr liest.
  const behoben: string[] = [];

  // „49 173 9722187" — ohne 0 und ohne Plus getippt. Das ist keine doppelte
  // Vorwahl, sondern die internationale Schreibweise ohne Plus. Die Nummer
  // wird gleich richtig gedeutet, aber ohne Meldung: es war kein Fehler.
  const internationalOhnePlus = !roh.includes('+') && !roh.startsWith('0');

  let text = roh;

  // „+49 +49 171…" — ab dem letzten Plus steht die Nummer, die gemeint war.
  const letztesPlus = text.lastIndexOf('+');
  if (letztesPlus > 0) {
    text = '+' + text.slice(letztesPlus + 1);
    behoben.push('die Vorwahl stand zweimal da („+49 +49 …")');
  }

  let nummer = deuten(text, land);
  if (!nummer) {
    return {
      status: 'pruefen', nummer: text, waehlbar: false, anzeige: text,
      land: null, art: 'unbekannt',
      hinweis: 'Die Nummer ist unvollständig — es fehlen Ziffern.',
    };
  }

  // Doppelte Landesvorwahl — der Fehler aus der Meldung.
  //
  // Weggenommen wird sie nur, wenn die Nummer dadurch nachweislich besser
  // wird: entweder war sie vorher gar nicht gültig, oder sie galt als
  // Festnetznummer und ist ohne die Dopplung eine gültige Mobilnummer. Genau
  // das ist die Falle „+49 49 176…", die als Leeraner Festnetznummer
  // durchgeht. Eine echte Nummer aus Leer („+49 491 6543") überlebt die
  // Prüfung dagegen unangetastet, weil „+49 16543" keine gültige Mobilnummer
  // ist.
  const vorwahl = getCountryCallingCode(nummer.country ?? land);
  const nsn = nummer.nationalNumber;
  if (nsn.startsWith(vorwahl) && nsn.length > vorwahl.length) {
    const ohneDopplung = deuten('+' + vorwahl + nsn.slice(vorwahl.length), land);
    const besser = ohneDopplung?.isValid()
      && (!nummer.isValid() || (istMobil(ohneDopplung) && !istMobil(nummer)));
    if (besser && ohneDopplung) {
      nummer = ohneDopplung;
      if (!internationalOhnePlus) {
        behoben.push('die Landesvorwahl stand zweimal da („+49 49 176…")');
      }
    }
  }

  const e164 = nummer.number as string;
  const gueltig = nummer.isValid();
  // Ist die Nummer ungültig, wird die Eingabe gezeigt und nicht eine daraus
  // konstruierte Fassung — sonst rätselt das Team, wo „+490176" herkommt.
  const anzeige = gueltig ? nummer.formatNational() : roh;
  const basis = {
    nummer: e164,
    waehlbar: gueltig && isE164(e164),
    anzeige,
    land: nummer.country ?? null,
    art: artVon(nummer),
  };

  if (!gueltig) {
    // Die Bibliothek kennt die gültigen Längen jedes Landes. Wenn sie die
    // Nummer ablehnt, fehlt oder klebt etwas dran.
    const zuLang = nsn.length > 11;
    return {
      ...basis,
      status: 'pruefen',
      hinweis: zuLang
        ? 'Die Nummer hat mehr Ziffern, als es für dieses Land gibt — '
          + 'steht da etwas doppelt?'
        : 'Die Nummer ist keine gültige Rufnummer — es fehlen Ziffern.',
    };
  }

  if (behoben.length > 0) {
    return {
      ...basis,
      status: 'korrigiert',
      hinweis: 'Automatisch berichtigt: ' + behoben.join(' und ') + '.',
    };
  }

  return { ...basis, status: 'ok', hinweis: null };
}

// Twilio erwartet E.164 (+CCNNNNNNN). Nationale Schreibweisen werden
// umgeschrieben; was sich nicht deuten lässt, kommt unverändert zurück, damit
// die Aufrufer selbst entscheiden können.
export function normalizePhone(input: string, land: CountryCode = STANDARD_LAND): string {
  return pruefeNummer(input, land).nummer;
}

const E164 = /^\+[1-9]\d{6,14}$/;
export function isE164(value: string): boolean {
  return E164.test(value);
}
